import { Component, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { switchMap, tap } from 'rxjs/operators';
import { of, throwError, Subscription } from 'rxjs';

// Import AuthService and related models
import { AuthService } from '../../core/services/auth.service';
import { LoginPayload, AuthResponse, GetApplicationsForActiveTermResponse, ApplicationData } from '../../core/models/auth.model';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    TranslateModule
  ],
  templateUrl: './login.component.html', 
  styleUrls: ['./login.component.css']
})
export class LoginComponent {
  loginData = {
    email: '', // school@example.com
    password: '' // password123
  };
  showPassword = false;
  loginError: string | null = null; 
  isLoading = false;
  private langChangeSubscription: Subscription | undefined;
  constructor(
    private router: Router,
    private translate: TranslateService,
    private authService: AuthService
  ) {}
  ngOnInit(): void {
    this.langChangeSubscription = this.translate.onLangChange.subscribe(() => {
      console.log('Language changed in LoginComponent.');
    });
  }

  ngOnDestroy(): void {
    if (this.langChangeSubscription) {
      this.langChangeSubscription.unsubscribe();
    }
  }
  toggleShowPassword(): void {
    this.showPassword = !this.showPassword;
  }

  onLogin(form: NgForm): void {
    this.loginError = null; 
    if (form.invalid) {
      alert(this.translate.instant('VALIDATION.FORM_INVALID'));
      return;
    }

    this.isLoading = true;
    const payload: LoginPayload = {
      email: this.loginData.email,
      password: this.loginData.password
    };

    this.authService.login(payload).pipe(
      switchMap((response: AuthResponse) => {
        if (response.status === 'ok' && response.token) {
          // console.log('登录成功! Token:', response.token);
          return this.authService.getApplicationsForActiveTerm().pipe(
            tap((appResponse: GetApplicationsForActiveTermResponse) => {
              if (appResponse.status === 'ok') {
                if (appResponse.applications && appResponse.applications.length > 0) {
                  // 获取第一个申请数据
                  const firstApplication = appResponse.applications[0];
                  if (firstApplication.status === 'approved') {
                    this.authService.setApplications(appResponse.applications); 
                    console.log('活动学期申请列表已存储至 AuthService，且第一个申请已批准。');
                  } else {
                    console.log('第一个活动学期申请状态不是approved，提示等待审批。');
                    throw new Error('APPLICATION_SUMMARY.WAITING_FOR_APPROVAL');
                  }
                } else {
                  console.log('活动学期申请列表为空，提示等待审批。');
                  this.router.navigate(['/information-school-enrollment']);
                  throw new Error('APPLICATION_SUMMARY.WAITING_FOR_APPROVAL');
                }
              } else {
                console.log('加载活动学期申请列表成功，但响应状态不OK:', appResponse.message || appResponse.error);
                throw new Error(appResponse.message || appResponse.error || this.translate.instant('APPLICATION_SUMMARY.ERROR_LOADING_APPLICATIONS'));
              }
            })
          );
        } else {
          const errorMessage = response.message || response.errors || this.translate.instant('LOGIN.LOGIN_FAILED_UNKNOWN');
          console.error('API认证失败:', errorMessage);
          return throwError(() => new Error(errorMessage));
        }
      })
    ).subscribe({
      next: () => {
        this.isLoading = false;
        this.router.navigate(['/home']);
      },
      error: (errorResponse) => {
        this.isLoading = false;
        // console.error('登录或后续数据加载请求错误:', errorResponse);

        let displayError = '';
        if (errorResponse instanceof Error) {
            displayError = errorResponse.message;
        } else if (errorResponse.error && typeof errorResponse.error === 'object') {
          const backendError = errorResponse.error;
          if (backendError.errors) {
            const errorMessages: string[] = [];
            for (const key in backendError.errors) {
              if (backendError.errors.hasOwnProperty(key) && Array.isArray(backendError.errors[key])) {
                errorMessages.push(...backendError.errors[key]);
              }
            }
            displayError = errorMessages.join(' ');
          } else if (backendError.message) {
            displayError = backendError.message;
          } else {
            displayError = this.translate.instant('LOGIN.LOGIN_REQUEST_FAILED');
          }
        } else if (typeof errorResponse.message === 'string' && errorResponse.message) {
            displayError = errorResponse.message;
        } else {
          displayError = this.translate.instant('LOGIN.LOGIN_REQUEST_FAILED');
        }

        // alert(displayError);
        this.loginError = displayError;
      }
    });
  }
}